import { CropIcon } from '@cw-elements/crop-icons';
import type { AppState } from '@protector/entities/__legacy__/redux.types';
import type { Season } from '@protector/entities/season/models';
import type { UUID } from '@protector/utils/types';
import { Skeleton } from 'antd';
import { COLOR_OPTIONS } from 'config/style/color-options';
import { BreadcrumbSeasonsGroup } from 'core/components/header/components/breadcrumb/breadcrumb-seasons.enum';
import STTypo from 'core/shared/typo';
import { TypeOptions } from 'core/shared/typo/typo.models';
import { selectAllSeasons } from 'entities/season/season.reducer';
import { find } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReactComponent as ChevronDownGlyph } from 'st-protector-icons/Glyph/chevron-down-glyph.svg';
import { SeasonPopoverTitleContainer, SeasonPopoverTitleWrapper } from './season-popover.style';

interface SeasonPopoverTitleProps {
  readonly mode: BreadcrumbSeasonsGroup;
  readonly selectedSeason: UUID | null;
}
export function SeasonPopoverTitle({ mode, selectedSeason }: SeasonPopoverTitleProps) {
  const isLoading = useSelector<AppState, boolean>(
    state => state.entities.company.isLoading || state.entities.property.isLoading || state.entities.season.isLoading
  );
  const seasons = useSelector<AppState, Season[]>(state => selectAllSeasons(state.entities.season));

  const [t] = useTranslation();

  const formatSeasonYear = (startDate: string, endDate: string): string => {
    return `${new Date(startDate).getFullYear().toString().slice(-2)} - ${new Date(endDate).getFullYear().toString().slice(-2)}`;
  };

  const title = useMemo(() => {
    if (mode === BreadcrumbSeasonsGroup.ALL_ACTIVES) {
      return (
        <STTypo fontFamily='Noto Sans' fontWeight={600}>
          {t('components.breadcrumb.all_crop_seasons')}
        </STTypo>
      );
    }
    const season = find(seasons, season => season.id === selectedSeason);
    if (selectedSeason && season) {
      return (
        <STTypo fontFamily='Noto Sans' fontWeight={600}>
          {formatSeasonYear(season.start_date, season.end_date)}
        </STTypo>
      );
    }

    return (
      <STTypo fontFamily='Noto Sans' fontWeight={600}>
        {t('header.seasons.no_selected_season.title')}
      </STTypo>
    );
  }, [mode, selectedSeason, seasons, t]);

  if (isLoading) {
    return <Skeleton active paragraph={false} title />;
  }

  return (
    <>
      <SeasonPopoverTitleContainer>
        <STTypo type={TypeOptions.P2} fontFamily='Noto Sans' color={COLOR_OPTIONS.NEUTRAL_60}>
          {t('components.breadcrumb.season_title')}
        </STTypo>
        <SeasonPopoverTitleWrapper data-testid='season-popover-title'>
          <CropIcon />
          <STTypo type={TypeOptions.P1} fontFamily='Noto Sans' fontWeight={600}>
            {title}
          </STTypo>
        </SeasonPopoverTitleWrapper>
      </SeasonPopoverTitleContainer>
      <ChevronDownGlyph />
    </>
  );
}
